'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import type { Media as MediaType } from '@/payload-types'
import { Media } from '@/components/Media'
import { ArrowIcon } from '@/components/icons/ArrowIcon'
import { cn } from '@/utilities/ui'

export type GalleryItem =
  | {
      key: string
      kind: 'upload'
      media: MediaType
      alt: string
      aspectRatio: number | null
    }
  | {
      key: string
      kind: 'url'
      src: string
      alt: string
      aspectRatio: number | null
    }

type Props = {
  items: GalleryItem[]
  variant: 'grid' | 'masonry'
  desktopColumns: 3 | 4
}

const gridLgCols: Record<3 | 4, string> = {
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
}

const masonryLgCols: Record<3 | 4, string> = {
  3: 'lg:columns-3',
  4: 'lg:columns-4',
}

const PAGER_BUTTON_CLASS =
  'cursor-pointer rounded-full bg-civo-lime p-3 text-civo-black transition duration-200 hover:bg-[#00e865] active:bg-[#00c953] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-black focus-visible:ring-offset-2 focus-visible:ring-offset-civo-lime motion-reduce:transition-none motion-reduce:active:scale-100'

const CLOSE_BUTTON_CLASS =
  'absolute right-4 top-4 cursor-pointer rounded-full bg-white/10 p-3 text-civo-white transition duration-200 hover:bg-white/20 active:bg-white/30 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-white focus-visible:ring-offset-2 focus-visible:ring-offset-civo-black motion-reduce:transition-none motion-reduce:active:scale-100'

const SWIPE_THRESHOLD_PX = 50

export const GalleryLightbox: React.FC<Props> = ({ items, variant, desktopColumns }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)
  const [activeImageLoaded, setActiveImageLoaded] = useState(false)

  const closeBtnRef = useRef<HTMLButtonElement | null>(null)
  const prevBtnRef = useRef<HTMLButtonElement | null>(null)
  const nextBtnRef = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLButtonElement | null>(null)
  const touchStartX = useRef<number | null>(null)

  const isOpen = activeIndex !== null
  const active = activeIndex !== null ? items[activeIndex] : null

  const close = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return current
      const target = previouslyFocused.current
      if (target) {
        window.requestAnimationFrame(() => target.focus())
      }
      return null
    })
  }, [])

  const goPrev = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null || items.length === 0) return current
      return (current + items.length - 1) % items.length
    })
  }, [items.length])

  const goNext = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null || items.length === 0) return current
      return (current + 1) % items.length
    })
  }, [items.length])

  // Reset URL-image load state whenever the active item changes.
  useEffect(() => {
    setActiveImageLoaded(false)
  }, [activeIndex])

  // Mount portal target after hydration so SSR returns null cleanly.
  useEffect(() => {
    setPortalTarget(document.body)
  }, [])

  // Body scroll lock + inert background while open.
  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const dialog = dialogRef.current
    const inertedSiblings: HTMLElement[] = []
    if (dialog) {
      Array.from(document.body.children).forEach((el) => {
        if (el === dialog || el.contains(dialog)) return
        const htmlEl = el as HTMLElement
        if (!htmlEl.inert) {
          htmlEl.inert = true
          inertedSiblings.push(htmlEl)
        }
      })
    }

    return () => {
      document.body.style.overflow = previousOverflow
      inertedSiblings.forEach((el) => {
        el.inert = false
      })
    }
  }, [isOpen])

  // Move focus to close button on open.
  useEffect(() => {
    if (!isOpen) return
    closeBtnRef.current?.focus()
  }, [isOpen])

  // Keyboard handlers + focus trap.
  useEffect(() => {
    if (!isOpen) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close()
        return
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        goPrev()
        return
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        goNext()
        return
      }
      if (event.key === 'Tab') {
        const first = closeBtnRef.current
        const last = nextBtnRef.current ?? closeBtnRef.current
        if (!first || !last) return
        const activeEl = document.activeElement as HTMLElement | null
        if (event.shiftKey && activeEl === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && activeEl === last) {
          event.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, close, goPrev, goNext])

  const onTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null
  }

  const onTouchEnd = (event: React.TouchEvent) => {
    if (touchStartX.current === null) return
    const endX = event.changedTouches[0]?.clientX
    if (typeof endX !== 'number') return
    const delta = endX - touchStartX.current
    touchStartX.current = null
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return
    if (delta > 0) goPrev()
    else goNext()
  }

  const containerClass =
    variant === 'grid'
      ? cn('grid grid-cols-2 md:grid-cols-3 gap-4', gridLgCols[desktopColumns])
      : cn('columns-2 md:columns-3 gap-4', masonryLgCols[desktopColumns])

  const renderThumbnail = (item: GalleryItem) => {
    // Masonry + URL with no override: flow at intrinsic size, no wrapper.
    if (variant === 'masonry' && item.kind === 'url' && item.aspectRatio === null) {
      return (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={item.src}
          alt={item.alt}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
          className="block w-full h-auto"
        />
      )
    }

    const wrapperStyle =
      variant === 'masonry' && item.aspectRatio ? { aspectRatio: item.aspectRatio } : undefined

    return (
      <div
        className={cn('relative w-full', variant === 'grid' && 'aspect-square')}
        style={wrapperStyle}
      >
        {item.kind === 'upload' ? (
          <Media
            resource={item.media}
            alt={item.alt}
            fill
            pictureClassName="absolute inset-0"
            imgClassName="object-cover"
          />
        ) : (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={item.src}
            alt={item.alt}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            className="absolute inset-0 size-full object-cover"
          />
        )}
      </div>
    )
  }

  const liveAnnouncement =
    isOpen && active
      ? `Image ${activeIndex! + 1} of ${items.length}${active.alt ? `: ${active.alt}` : ''}`
      : ''

  const dialog = active && activeIndex !== null && (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
      onClick={(event) => {
        if (event.target === event.currentTarget) close()
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-civo-black/90 p-4 motion-reduce:transition-none"
    >
      <span className="absolute left-1/2 top-4 -translate-x-1/2 text-civo-white body-medium">
        Image {activeIndex + 1} of {items.length}
      </span>

      <button
        ref={closeBtnRef}
        type="button"
        onClick={close}
        aria-label="Close gallery"
        className={CLOSE_BUTTON_CLASS}
      >
        <X className="size-6" aria-hidden="true" />
      </button>

      <div
        className="relative max-h-[90dvh] max-w-[90vw]"
        onClick={(event) => event.stopPropagation()}
      >
        {active.kind === 'upload' ? (
          <Media
            resource={active.media}
            alt={active.alt}
            imgClassName="h-auto w-auto max-h-[90dvh] max-w-[90vw] object-contain"
          />
        ) : (
          <>
            {!activeImageLoaded && (
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-[12px] bg-civo-black/40"
              />
            )}
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={active.src}
              alt={active.alt}
              onLoad={() => setActiveImageLoaded(true)}
              referrerPolicy="no-referrer"
              className={cn(
                'max-h-[90dvh] max-w-[90vw] object-contain transition-opacity duration-200 motion-reduce:transition-none',
                activeImageLoaded ? 'opacity-100' : 'opacity-0',
              )}
            />
          </>
        )}
      </div>

      {items.length > 1 && (
        <div className="absolute bottom-4 right-4 flex gap-2">
          <button
            ref={prevBtnRef}
            type="button"
            onClick={goPrev}
            aria-label="Previous image"
            className={PAGER_BUTTON_CLASS}
          >
            <ArrowIcon className="size-6" direction="left" stroke="currentColor" />
          </button>
          <button
            ref={nextBtnRef}
            type="button"
            onClick={goNext}
            aria-label="Next image"
            className={PAGER_BUTTON_CLASS}
          >
            <ArrowIcon className="size-6" direction="right" stroke="currentColor" />
          </button>
        </div>
      )}
    </div>
  )

  // Wrap the live region and dialog in a single body-child container.
  // The inert sweep skips any sibling that contains the dialog, so the
  // live region (inside this wrapper) stays in the accessibility tree
  // while the dialog is open.
  const portalContent = (
    <div>
      <span aria-live="polite" aria-atomic="true" className="sr-only">
        {liveAnnouncement}
      </span>
      {dialog}
    </div>
  )

  return (
    <>
      <div className={containerClass}>
        {items.map((item, index) => (
          <button
            key={item.key}
            type="button"
            onClick={(event) => {
              previouslyFocused.current = event.currentTarget
              setActiveIndex(index)
            }}
            aria-label={`Open image ${index + 1}${item.alt ? `: ${item.alt}` : ''}`}
            className={cn(
              'block w-full cursor-pointer overflow-hidden rounded-[12px] transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-civo-black focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100',
              variant === 'masonry' && 'mb-4 break-inside-avoid',
            )}
          >
            {renderThumbnail(item)}
          </button>
        ))}
      </div>

      {portalTarget && createPortal(portalContent, portalTarget)}
    </>
  )
}
